/**
 * SEO Backfill Autopilot Status Banner
 *
 * Feb 24, 2026. Read-only dashboard widget that polls the server for the
 * most recent SEO backfill autopilot report and surfaces:
 *   • कितने blogs successful
 *   • कितने failed
 *   • Representative errors
 *
 * Zero manual control — no button, no trigger. The autopilot fires itself
 * once on server startup after each deploy (bumping SEO_BACKFILL_VERSION
 * forces a re-run). This component just mirrors the DB report back to the
 * admin so you can see "मेरा काम हो गया" at a glance.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";

const API_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

export default function SeoBackfillBanner() {
  const [state, setState] = useState({ loading: true });

  useEffect(() => {
    let cancelled = false;
    const token = localStorage.getItem("admin_token") || localStorage.getItem("token");

    async function fetchReport() {
      try {
        const { data } = await axios.get(
          `${API_BASE}/api/admin/seo-backfill/latest`,
          token ? { headers: { Authorization: `Bearer ${token}` } } : undefined,
        );
        if (!cancelled) setState({ loading: false, data });
      } catch (e) {
        if (!cancelled) setState({ loading: false, error: e?.response?.data?.detail || e.message });
      }
    }
    fetchReport();
    // Light polling in case autopilot is still running when admin lands
    const t = setInterval(fetchReport, 15000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, []);

  if (state.loading) {
    return (
      <div
        className="mb-6 rounded-xl border border-gold/20 bg-black/30 px-4 py-3 text-sm text-text-muted"
        data-testid="seo-backfill-banner-loading"
      >
        SEO Autopilot status check हो रहा है…
      </div>
    );
  }

  if (state.error) {
    return (
      <div
        className="mb-6 rounded-xl border border-amber-500/30 bg-amber-500/5 px-4 py-3 text-sm text-amber-200"
        data-testid="seo-backfill-banner-error"
      >
        SEO Autopilot status read नहीं हो पाया: {state.error}
      </div>
    );
  }

  const data = state.data || {};
  if (data.status === "pending") {
    return (
      <div
        className="mb-6 rounded-xl border border-blue-500/30 bg-blue-500/5 px-4 py-3 text-sm text-blue-200"
        data-testid="seo-backfill-banner-pending"
      >
        🔄 SEO Autopilot server startup के साथ चल रहा है। कुछ ही मिनटों में report यहाँ दिखेगी।
      </div>
    );
  }

  const stats = data.report?.stats || {};
  const errorsLen = (stats.errors || []).length;
  const ok = (stats.prerender_ok || 0);
  const fail = (stats.prerender_fail || 0);
  const total = stats.total || 0;
  const titlesFilled = stats.meta_title_filled || 0;
  const descsFilled = stats.meta_desc_filled || 0;
  const faqCount = stats.faq_schemas_present || 0;
  const completedAt = data.report?.completed_at
    ? new Date(data.report.completed_at).toLocaleString("hi-IN", {
        day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
      })
    : "—";

  const healthClass =
    fail === 0 && errorsLen === 0
      ? "border-emerald-500/40 bg-emerald-500/5 text-emerald-100"
      : "border-amber-500/40 bg-amber-500/5 text-amber-100";

  return (
    <div
      className={`mb-6 rounded-xl border px-5 py-4 ${healthClass}`}
      data-testid="seo-backfill-banner"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="font-heading text-base mb-1">
            ✨ SEO Autopilot — आखरी run complete
          </div>
          <div className="text-xs opacity-80">
            {completedAt} · version {data.report?.version || 1}
          </div>
        </div>
        <div className="flex gap-4 text-sm flex-wrap">
          <Metric label="Total blogs" value={total} testid="seo-metric-total" />
          <Metric label="Prerender OK" value={ok} testid="seo-metric-ok" />
          <Metric label="Prerender Fail" value={fail}
                  highlight={fail > 0} testid="seo-metric-fail" />
          <Metric label="Meta Titles भरे" value={titlesFilled} testid="seo-metric-titles" />
          <Metric label="Meta Descs भरे" value={descsFilled} testid="seo-metric-descs" />
          <Metric label="FAQ Schemas" value={faqCount} testid="seo-metric-faq" />
        </div>
      </div>
      {errorsLen > 0 && (
        <details className="mt-3 text-xs opacity-90" data-testid="seo-backfill-errors">
          <summary className="cursor-pointer underline-offset-2 hover:underline">
            {errorsLen} error{errorsLen > 1 ? "s" : ""} — details
          </summary>
          <ul className="mt-2 ml-4 list-disc space-y-1 max-h-40 overflow-auto">
            {(stats.errors || []).slice(0, 20).map((e, i) => (
              <li key={i}>
                <code className="text-amber-200">/{e.slug}</code> ({e.stage}): {e.error}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

function Metric({ label, value, highlight, testid }) {
  return (
    <div className="flex flex-col leading-tight" data-testid={testid}>
      <span className={`font-heading text-xl ${highlight ? "text-amber-300" : ""}`}>
        {value}
      </span>
      <span className="text-[11px] opacity-70">{label}</span>
    </div>
  );
}
